import { isInApp, share, openUrlInBrowser } from "@/libs/comeAppSDK";
import { isIOS } from "./browser";

export enum ShareType {
  FACEBOOK = 'facebook',
  WHATSAPP = 'whatsapp',
  TELEGRAM = 'telegram',
};

interface ShareData {
  type?: ShareType;
  // 客户端apk使用
  title?: string;
  text?: string;
  url?: string;
  // 客户端apk使用
  apkImage?: Blob;
}

// Facebook App ID
const FACEBOOK_APP_ID = '1518095526065071';

// Facebook SDK 响应类型
interface FacebookShareResponse {
  error_message?: string;
  post_id?: string;
}

interface FacebookSendResponse {
  error_message?: string;
  success?: boolean;
}

// 声明 Facebook SDK 类型
declare global {
  interface Window {
    FB?: {
      init: (config: { appId: string; version: string; xfbml?: boolean; }) => void;
      ui: (
        params: {
          method: string;
          href?: string;
          link?: string;
          quote?: string;
        },
        callback?: (response: FacebookShareResponse | FacebookSendResponse) => void
      ) => void;
      getLoginStatus: (callback: (response: unknown) => void) => void;
    };
    fbAsyncInit?: () => void;
  }
}

// 加载 Facebook SDK
const loadFacebookSDK = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // 如果已经加载了 SDK，直接 resolve
    if (window.FB) {
      resolve();
      return;
    }

    // 如果已经在加载中，等待加载完成
    if (window.fbAsyncInit) {
      const originalInit = window.fbAsyncInit;
      window.fbAsyncInit = () => {
        originalInit();
        resolve();
      };
      return;
    }

    // 设置初始化回调
    window.fbAsyncInit = () => {
      if (window.FB) {
        window.FB.init({
          appId: FACEBOOK_APP_ID,
          version: 'v18.0',
          xfbml: true,
        });
        resolve();
      } else {
        reject(new Error('Facebook SDK failed to load'));
      }
    };

    // 动态加载 Facebook SDK 脚本
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => {
      reject(new Error('Failed to load Facebook SDK'));
    };
    document.body.appendChild(script);
  });
};

// !!fb的h5分享官方有问题
const shareToFacebook = async (data: ShareData) => {
  const baseUrl = data?.url || window.location.href;
  const redirectUri = encodeURIComponent(window.location.href);

  // 如果有文案，将文案附加到 URL 的查询参数中（作为 fallback）
  // Facebook SDK 的 send 方法不支持直接传递文案，但可以通过 URL 传递
  let urlWithText = baseUrl;
  if (data?.text) {
    const urlObj = new URL(baseUrl, window.location.origin);
    urlObj.searchParams.set('text', data.text);
    urlWithText = urlObj.toString();
  }

  const encodedUrl = encodeURIComponent(urlWithText);

  // Web dialog 链接，支持文案和 URL（文案已包含在 URL 中）
  const webMessengerLink = `https://www.facebook.com/dialog/send?app_id=${FACEBOOK_APP_ID}&link=${encodedUrl}&redirect_uri=${redirectUri}`;

  try {
    // 优先使用 Facebook SDK（兼容 H5 和桌面端）
    // 在移动端 H5 中，SDK 会自动处理：如果已安装 Messenger app 则打开 app，否则使用 web dialog
    // 在桌面端，SDK 会使用 web dialog
    await loadFacebookSDK();

    if (!window.FB) {
      throw new Error('Facebook SDK not available');
    }

    // 使用 Facebook SDK 的 send 方法
    // 注意：send 方法不支持直接传递文案，但会将 URL 传递过去
    // 如果 URL 中包含文案信息，可以在消息中显示
    // 移动端：如果已安装 Messenger app，会打开 app 让用户选择联系人
    // 如果未安装 Messenger app，会在 H5 浏览器中打开 web dialog
    // 桌面端：直接打开 web dialog
    window.FB.ui(
      {
        method: 'send',
        link: urlWithText, // 使用包含文案的 URL
      },
      (response: FacebookSendResponse) => {
        if (response && !response.error_message) {
          console.log('Facebook send successful', response);
        } else {
          console.error('Facebook send error:', response?.error_message);
          // SDK 调用失败时，降级到 web dialog（包含文案）
          window.open(webMessengerLink, '_blank', 'width=626,height=436');
        }
      }
    );
  } catch (error) {
    console.error('Failed to share to Facebook via SDK:', error);
    // 降级方案：直接使用 web dialog（兼容所有 H5 环境，包含文案和 URL）
    window.open(webMessengerLink, '_blank', 'width=626,height=436');
  }
};

const shareToWhatsapp = async (data: ShareData) => {
  // 合并文本和链接
  let fullText = `${data?.text}`;
  if (data?.url) {
    fullText += `\n${data?.url}`;
  }
  const encodedText = encodeURIComponent(fullText);
  // 使用 wa.me 通用链接作为回退
  const whatsappLink = `https://wa.me/?text=${encodedText}`;
  location.href = whatsappLink;
};

const shareToTelegram = async (data: ShareData) => {
  // 确保 URL 和 TEXT 都经过 URL 编码
  const encodedUrl = encodeURIComponent('');  // 这个参数一定要携带，否则无法跳转到app选择联系人
  // 需求：文案在前，URL 在后
  const combined = [data?.text || '', data?.url || ''].filter(Boolean).join('\n').trim();
  const encodedCombined = encodeURIComponent(combined);
  // Telegram 分享链接格式
  // const telegramShareLink = `https://t.me/share/url?url=${encodedUrl}&text=${encodedCombined}`;
  const telegramShareLink = `tg://msg?url=${encodedUrl}&text=${encodedCombined}`;
  location.href = telegramShareLink;
};

function toQuery(params?: Record<string, string | number | undefined>) {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) usp.append(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

/**
 * 获取三方应用安装地址（含平台处理）
 * - Telegram: 官网下载页
 * - WhatsApp: 官网下载页
 * - Facebook:
 *   - Android: 官网下载页
 *   - iOS: App Store（直接拼接查询参数；是否被识别取决于 Apple 的策略）
 */
export function getInstallUrl(type: ShareType, fbParams?: Record<string, string | number | undefined>): string {
  switch (type) {
    case ShareType.TELEGRAM:
      return 'https://telegram.org';
    case ShareType.WHATSAPP:
      return 'https://www.whatsapp.com/download';
    case ShareType.FACEBOOK: {
      if (isIOS()) {
        // iOS: App Store，附带自定义参数（可能不会被 Apple 解析，但按需附加）
        const base = 'https://apps.apple.com/app/facebook/id284882215';
        return `${base}${toQuery(fbParams)}`;
      }
      // Android: 使用 Facebook 官方 android_upgrade 页面，而非 Google Play
      const base = 'https://www.facebook.com/android_upgrade';
      // 该页面常见带有 utm 参数，这里统一通过 fbParams 透传
      return `${base}${toQuery(fbParams || { utm: '' })}`;
    }
    default:
      return '';
  }
}
/**
 * 分享到平台
 * @example shareToPlatform({
 *   type: ShareType.FACEBOOK, // 分享类型
 *   url: 'https://www.facebook.com', // 分享链接
 *   text: 'test share link', // 分享文本
 *   files: new File([blob as BlobPart], 'image.png', { type: 'image/png' }), // 浏览器使用
 *   apkImage: new Blob([new Uint8Array([0x00, 0x01, 0x02, 0x03])], { type: 'image/png' }), // 客户端apk使用
 * });
 * @param data 分享数据
 */
export const shareToPlatform = async (data: ShareData, autoJump = true) => {
  console.log("share data=========>", data);
  if (isInApp()) {
    await share({
      title: data?.title || '',
      description: data?.text || '',
      link: data?.url || '',
      type: (data?.type || '') as ShareType,
      image: data?.apkImage || undefined,
    }).then((_status) => {
      if (autoJump && !_status) {
        const installUrl = getInstallUrl(data.type as ShareType);
        if (!installUrl) {
          return;
        }
        // location.href = installUrl;
        openUrlInBrowser(installUrl);
      }
    });
  } else {
    switch (data?.type) {
      case ShareType.FACEBOOK:
        await shareToFacebook(data);
        break;
      case ShareType.WHATSAPP:
        await shareToWhatsapp(data);
        break;
      case ShareType.TELEGRAM:
        await shareToTelegram(data);
        break;
    }
  }
};